import * as THREE from "three";

// Deterministic PRNG so scenery layout is stable across renders
export const mulberry32 = (seed: number) => {
  let a = seed;
  return () => {
    a |= 0;
    a = (a + 0x6d2b79f5) | 0;
    let t = Math.imul(a ^ (a >>> 15), 1 | a);
    t = (t + Math.imul(t ^ (t >>> 7), 61 | t)) ^ t;
    return ((t ^ (t >>> 14)) >>> 0) / 4294967296;
  };
};

const pixelTextureCache = new Map<string, THREE.Texture>();

/** Renders a tiny canvas with `draw`, then wraps it as a crisp nearest-filtered texture. */
const makePixelTexture = (
  key: string,
  size: number,
  draw: (ctx: CanvasRenderingContext2D, size: number) => void,
): THREE.Texture => {
  const cached = pixelTextureCache.get(key);
  if (cached) return cached;

  const canvas = document.createElement("canvas");
  canvas.width = size;
  canvas.height = size;
  const ctx = canvas.getContext("2d")!;
  draw(ctx, size);

  const texture = new THREE.CanvasTexture(canvas);
  texture.magFilter = THREE.NearestFilter;
  texture.minFilter = THREE.NearestFilter;
  texture.generateMipmaps = false;
  texture.wrapS = THREE.RepeatWrapping;
  texture.wrapT = THREE.RepeatWrapping;
  pixelTextureCache.set(key, texture);
  return texture;
};

const rngFill = (ctx: CanvasRenderingContext2D, size: number, colors: string[], seed: number) => {
  const rand = mulberry32(seed);
  for (let y = 0; y < size; y++) {
    for (let x = 0; x < size; x++) {
      ctx.fillStyle = colors[Math.floor(rand() * colors.length)];
      ctx.fillRect(x, y, 1, 1);
    }
  }
};

export const getGrassTexture = () =>
  makePixelTexture("grass", 16, (ctx, size) => {
    rngFill(ctx, size, ["#8fbf4a", "#83b342", "#96c957", "#79a83a"], 1);
  });

export const getBarkTexture = () =>
  makePixelTexture("bark", 8, (ctx, size) => {
    rngFill(ctx, size, ["#6b4a30", "#5e3f28", "#77543a"], 2);
  });

export const getLeafTexture = (variant: number) =>
  makePixelTexture(`leaf-${variant}`, 8, (ctx, size) => {
    const palettes = [
      ["#4c8f3b", "#3f7a30", "#579c47"],
      ["#5a9c3f", "#4a8534", "#68ab4d"],
      ["#3f8a55", "#347146", "#4c9c63"],
    ];
    rngFill(ctx, size, palettes[variant % palettes.length], 10 + variant);
  });

export const getSkinTexture = () =>
  makePixelTexture("skin", 8, (ctx, size) => {
    rngFill(ctx, size, ["#e0a878", "#d49966", "#e8b488"], 6);
  });

export const getShirtTexture = () =>
  makePixelTexture("shirt", 8, (ctx, size) => {
    rngFill(ctx, size, ["#91bd3a", "#7da432", "#a0cc4a"], 7);
  });

export const getPantsTexture = () =>
  makePixelTexture("pants", 8, (ctx, size) => {
    rngFill(ctx, size, ["#3f4a5c", "#374254", "#465368"], 8);
  });

export const getFenceTexture = () =>
  makePixelTexture("fence", 8, (ctx, size) => {
    ctx.fillStyle = "#8a5a34";
    ctx.fillRect(0, 0, size, size);
    const rand = mulberry32(11);
    for (let y = 0; y < size; y++) {
      for (let x = 0; x < size; x++) {
        if (x % 3 === 0) {
          ctx.fillStyle = "#6f4526";
          ctx.fillRect(x, y, 1, 1);
        } else if (rand() < 0.15) {
          ctx.fillStyle = "#9a6a3e";
          ctx.fillRect(x, y, 1, 1);
        }
      }
    }
  });

export const getWaterTexture = () =>
  makePixelTexture("water", 8, (ctx, size) => {
    rngFill(ctx, size, ["#3fa9d8", "#4fb8e8", "#2f8fc0", "#5fc4f0"], 13);
  });

export const getRockTexture = () =>
  makePixelTexture("rock", 8, (ctx, size) => {
    rngFill(ctx, size, ["#9a9a94", "#87877f", "#a8a8a1", "#77776f"], 3);
  });

export const getMountainTexture = () =>
  makePixelTexture("mountain", 8, (ctx, size) => {
    rngFill(ctx, size, ["#8f9bb5", "#7c88a3", "#9dabc7"], 4);
  });

export const getCloudTexture = () =>
  makePixelTexture("cloud", 16, (ctx, size) => {
    ctx.clearRect(0, 0, size, size);
    const rand = mulberry32(5);
    const cx = size / 2;
    const cy = size / 2;
    for (let y = 0; y < size; y++) {
      for (let x = 0; x < size; x++) {
        const d = Math.hypot(x - cx, y - cy) / (size / 2);
        if (d < 0.85 + rand() * 0.15) {
          ctx.fillStyle = "rgba(255,255,255,0.92)";
          ctx.fillRect(x, y, 1, 1);
        }
      }
    }
  });

/** Fallback picture for projects without screenshots — pixel-bordered placeholder with the title. */
export const getPlaceholderTexture = (title: string, accent = "#91bd3a") => {
  const key = `placeholder-${title}`;
  const cached = pixelTextureCache.get(key);
  if (cached) return cached;

  const canvas = document.createElement("canvas");
  canvas.width = 256;
  canvas.height = 512;
  const ctx = canvas.getContext("2d")!;

  ctx.fillStyle = "#1f2a1a";
  ctx.fillRect(0, 0, 256, 512);

  // pixel border
  ctx.fillStyle = accent;
  const border = 10;
  ctx.fillRect(0, 0, 256, border);
  ctx.fillRect(0, 512 - border, 256, border);
  ctx.fillRect(0, 0, border, 512);
  ctx.fillRect(256 - border, 0, border, 512);

  // simple pixel "code brackets" glyph
  ctx.font = "bold 90px monospace";
  ctx.fillStyle = accent;
  ctx.textAlign = "center";
  ctx.textBaseline = "middle";
  ctx.fillText("</>", 128, 220);

  ctx.font = "bold 20px monospace";
  ctx.fillStyle = "#ffffff";
  const words = title.split(" ");
  let line = "";
  let y = 320;
  const lines: string[] = [];
  words.forEach((w) => {
    const test = line ? `${line} ${w}` : w;
    if (ctx.measureText(test).width > 210) {
      lines.push(line);
      line = w;
    } else {
      line = test;
    }
  });
  if (line) lines.push(line);
  lines.slice(0, 4).forEach((l) => {
    ctx.fillText(l, 128, y);
    y += 28;
  });

  const texture = new THREE.CanvasTexture(canvas);
  texture.magFilter = THREE.NearestFilter;
  texture.minFilter = THREE.LinearFilter;
  pixelTextureCache.set(key, texture);
  return texture;
};
